import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { supabase } from "@/lovable-demo/runtime/client";
import type { AppRole } from "@/lovable-demo/lib/governance/types";
import {
  requestFromRow,
  signoffFromRow,
  type PfmeaChangeRequest,
  type PfmeaSignoff,
} from "./pfmea-signoff";

export const pfmeaSignoffKey = (recordId: string) =>
  ["lovable-demo", "pfmea-signoffs", recordId] as const;
export const myIdentityKey = ["lovable-demo", "my-identity"] as const;

export function usePfmeaSignoffs(recordId: string) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: pfmeaSignoffKey(recordId),
    queryFn: async (): Promise<{ requests: PfmeaChangeRequest[]; signoffs: PfmeaSignoff[] }> => {
      const { data: reqs, error } = await supabase
        .from("pfmea_change_requests")
        .select("*")
        .eq("record_id", recordId)
        .order("created_at");
      if (error) throw new Error(error.message);
      const ids = (reqs ?? []).map((r) => r.id);
      const { data: offs, error: e2 } = ids.length
        ? await supabase
            .from("pfmea_signoffs")
            .select("*")
            .in("request_id", ids)
            .order("created_at")
        : { data: [], error: null };
      if (e2) throw new Error(e2.message);
      return {
        requests: (reqs ?? []).map(requestFromRow),
        signoffs: (offs ?? []).map(signoffFromRow),
      };
    },
  });
  return {
    requests: query.data?.requests ?? [],
    signoffs: query.data?.signoffs ?? [],
    loading: query.isLoading,
    refresh: () => qc.invalidateQueries({ queryKey: pfmeaSignoffKey(recordId) }),
  };
}

export function useMyIdentity() {
  const queryClient = useQueryClient();
  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange(() => {
      void queryClient.invalidateQueries({ queryKey: myIdentityKey });
    });
    return () => data.subscription.unsubscribe();
  }, [queryClient]);
  const q = useQuery({
    queryKey: myIdentityKey,
    queryFn: async () => {
      const { data } = await supabase.auth.getUser();
      const user = data.user;
      if (!user)
        return { userId: null as string | null, email: "", name: "", roles: [] as AppRole[] };
      const { fetchMyRoles } = await import("@/lovable-demo/lib/governance/roles");
      const [roles, profile] = await Promise.all([
        fetchMyRoles(),
        supabase.from("profiles").select("full_name, job_title").eq("id", user.id).maybeSingle(),
      ]);
      return {
        userId: user.id as string | null,
        email: user.email ?? "",
        name: [profile.data?.full_name, profile.data?.job_title].filter(Boolean).join(", "),
        roles,
      };
    },
  });
  return q.data ?? { userId: null, email: "", name: "", roles: [] as AppRole[] };
}
